import {
  type ResolvedItem,
  resolveGroupMemberships,
} from "@/lib/measurement-utils";

import type {
  BodyMeasure,
  BodySegment,
  MeasurementField,
  MeasurementGroupWithFields,
} from "@/types/measurement";

// InBody's own order on its result sheet, so the rows read like the printout.
export const BODY_SEGMENTS: BodySegment[] = [
  "RIGHT_ARM",
  "LEFT_ARM",
  "TRUNK",
  "RIGHT_LEG",
  "LEFT_LEG",
];

export const BODY_SEGMENT_LABELS: Record<BodySegment, string> = {
  TRUNK: "Trunk",
  LEFT_ARM: "Left arm",
  RIGHT_ARM: "Right arm",
  LEFT_LEG: "Left leg",
  RIGHT_LEG: "Right leg",
};

export const BODY_MEASURES: BodyMeasure[] = [
  "LEAN_MASS",
  "LEAN_PCT_OF_NORMAL",
  "FAT_MASS",
  "FAT_PCT_OF_NORMAL",
];

export const BODY_MEASURE_LABELS: Record<BodyMeasure, string> = {
  LEAN_MASS: "Lean mass",
  LEAN_PCT_OF_NORMAL: "Lean % of normal",
  FAT_MASS: "Fat mass",
  FAT_PCT_OF_NORMAL: "Fat % of normal",
};

export function isBodyGroup(
  group: Pick<MeasurementGroupWithFields, "displayConfig"> | null | undefined,
): boolean {
  return group?.displayConfig?.type === "BODY";
}

export interface BodySlotEntry {
  field: MeasurementField;
}

export type BodySlots = Record<
  BodySegment,
  Partial<Record<BodyMeasure, BodySlotEntry>>
>;

export interface BodyLayout {
  slots: BodySlots;
  measuresPresent: BodyMeasure[];
}

function emptySlots(): BodySlots {
  return {
    TRUNK: {},
    LEFT_ARM: {},
    RIGHT_ARM: {},
    LEFT_LEG: {},
    RIGHT_LEG: {},
  };
}

type FieldItem = Extract<ResolvedItem, { type: "field" }>;

function fieldsInTreeOrder(group: MeasurementGroupWithFields): FieldItem[] {
  const items = resolveGroupMemberships(group, {
    includeSectionHeaders: false,
  }).filter((item): item is FieldItem => item.type === "field");

  const seen = new Set<string>();
  const deduped: FieldItem[] = [];
  for (const item of items) {
    if (seen.has(item.field.id)) continue;
    seen.add(item.field.id);
    deduped.push(item);
  }
  return deduped;
}

export function bodyGroupHasTaggedFields(
  group: MeasurementGroupWithFields,
): boolean {
  return fieldsInTreeOrder(group).some(
    (item) => !!item.field.displayConfig?.body,
  );
}

export function canRenderBodyGroup(group: MeasurementGroupWithFields): boolean {
  return isBodyGroup(group) && bodyGroupHasTaggedFields(group);
}

export function resolveBodyLayout(
  group: MeasurementGroupWithFields,
): BodyLayout {
  const slots = emptySlots();
  const measuresPresentSet = new Set<BodyMeasure>();

  for (const { field } of fieldsInTreeOrder(group)) {
    const tag = field.displayConfig?.body;
    if (!tag) continue;

    const bySegment = slots[tag.segment];
    if (!bySegment[tag.measure]) {
      bySegment[tag.measure] = { field };
    }
    measuresPresentSet.add(tag.measure);
  }

  return {
    slots,
    measuresPresent: BODY_MEASURES.filter((m) => measuresPresentSet.has(m)),
  };
}
